import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Dropdown, Button, Offcanvas } from "react-bootstrap";
import { BsBellFill, BsList } from "react-icons/bs";
import API from "../services/api";
import "./EmployeeMainLayout.css";
import "./PanelMobileChrome.css";
import { resolveProfilePhoto } from "../utils/profilePhoto";
import GstApiDueNotice from "./GstApiDueNotice";
import {
  clearStoredSession,
  getStoredUser,
  redirectToLogin,
  SESSION_USER_UPDATED_EVENT,
  updateStoredUser,
} from "../utils/sessionUser";

const readStoredPermissions = () => {
  try {
    const direct = JSON.parse(localStorage.getItem("permissions") || "[]");
    if (Array.isArray(direct) && direct.length) return direct;
  } catch (_err) {
    // fall through to user payload
  }
  try {
    const storedUser = getStoredUser();
    const fromUser =
      storedUser?.employeeRole?.permissions ||
      storedUser?.permissions ||
      [];
    return Array.isArray(fromUser) ? fromUser : [];
  } catch (_err) {
    return [];
  }
};

const readStoredDepartmentName = () => {
  const storedUser = getStoredUser();
  return String(storedUser?.department?.name || "");
};

const readStoredRoleName = () => {
  const storedUser = getStoredUser();
  return String(storedUser?.employeeRole?.name || "");
};

const EmployeeMainLayout = () => {
  const [notificationCount, setNotificationCount] = useState(0);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [permissions, setPermissions] = useState(() => readStoredPermissions());
  const [permReady, setPermReady] = useState(() => readStoredPermissions().length > 0);
  const [departmentName, setDepartmentName] = useState(() => readStoredDepartmentName());
  const [roleName, setRoleName] = useState(() => readStoredRoleName());
  const [user, setUser] = useState(() => getStoredUser());
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await API.get("/employee/notifications"); 
        const rows = Array.isArray(res.data) ? res.data : [];
        setNotificationCount(rows.filter((item) => !item.seen).length);
      } catch (err) {
        console.error("Error fetching notifications:", err);
      }
    };

    fetchNotifications();
    const intervalId = window.setInterval(fetchNotifications, 15000);
    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const res = await API.get("/auth/me");
        const perms =
          res.data?.employeeRole?.permissions ||
          res.data?.permissions ||
          [];
        const dept = res.data?.department?.name || "";
        const role = res.data?.employeeRole?.name || "";
        setPermissions(Array.isArray(perms) ? perms : []);
        setDepartmentName(String(dept));
        setRoleName(String(role));
        localStorage.setItem("permissions", JSON.stringify(Array.isArray(perms) ? perms : []));
      } catch (err) {
        console.error("Error fetching permissions:", err);
        const fallbackPerms = readStoredPermissions();
        setPermissions(fallbackPerms);
        setDepartmentName(readStoredDepartmentName());
        setRoleName(readStoredRoleName());
        if (!fallbackPerms.length) localStorage.setItem("permissions", JSON.stringify([]));
      } finally {
        setPermReady(true);
      }
    };

    fetchPermissions();
  }, []);

  useEffect(() => {
    const syncUser = () => {
      setUser(getStoredUser());
      const storedPerms = readStoredPermissions();
      if (storedPerms.length) setPermissions(storedPerms);
      setDepartmentName(readStoredDepartmentName());
      setRoleName(readStoredRoleName());
    };
    window.addEventListener(SESSION_USER_UPDATED_EVENT, syncUser);
    window.addEventListener("storage", syncUser);
    return () => {
      window.removeEventListener(SESSION_USER_UPDATED_EVENT, syncUser);
      window.removeEventListener("storage", syncUser);
    };
  }, []);

  useEffect(() => {
    const syncEmployeeProfile = async () => {
      try {
        const { data } = await API.get("/employee/profile/me");
        const next = updateStoredUser({
          fullName: data?.fullName || user?.fullName || "Employee",
          email: data?.email || user?.email || "",
          mobile: data?.mobile || user?.mobile || "",
          photo: data?.photo || user?.photo || "",
          photoUpdatedAt: Date.now(),
        });
        setUser(next);
      } catch (_err) {
        // keep existing session user
      }
    };
    syncEmployeeProfile();
    // run once on mount; employee profile page handles further updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    clearStoredSession();
    redirectToLogin();
  };

  const routeTitles = {
  "/employee/dashboard": "Dashboard",
  "/employee/attendance": "Attendance",
  "/employee/idcard": "ID Card",
  "/employee/my-vendors": "Assigned Vendors",
  "/employee/my-vendors/:id": "Assigned Vendor Profile",
  "/employee/my-suppliers": "Assigned Suppliers",
  "/employee/my-suppliers/:id": "Assigned Supplier Profile",
  "/employee/suppliers/:id": "Connected Supplier Profile",
  "/employee/requirements": " Assigned Requirements",
  "/employee/gallery": "Product Gallery",
  "/employee/reports": "Reports",
  "/employee/salary": "My Salary",
  "/employee/agreement": "Employee Agreement",
  "/employee/payment-history": "Payment History",
  "/employee/guard/dashboard": "Guard Dashboard",
  "/employee/guard/attendance": "Guard Attendance",
  "/employee/guard/attendance/history": "Guard Attendance History",
  "/employee/guard/vehicles": "Guard Vehicle Check",
  "/employee/guard/logs": "Guard Vehicle Logs",
  "/employee/accounts/phonepe-transactions": "PhonePe Transactions",
  "/employee/accounts": "Accounts",
  "/employee/accounts/parties": "Party Ledger",
  "/employee/accounts/invoices": "Invoices",
  "/employee/accounts/expenses": "Expenses",
	  "/employee/accounts/vendor-bills": "Vendor Bills",
	  "/employee/accounts/quality-payments": "Quality Payments",
	  "/employee/accounts/vendor-incoming-payments": "Vendor Incoming Payments",
	  "/employee/accounts/supplier-payables": "Supplier Payables",
  "/employee/accounts/payments": "Payments",
  "/employee/accounts/government-security": "Government Security",
  "/employee/accounts/payroll": "Payroll",
  "/employee/accounts/taxes": "GST / TDS",
  "/employee/accounts/reports": "Financial Reports",
  "/employee/accounts/audit-logs": "Audit Logs",
  "/employee/accounts/contractor-work-entries": "Contractor Payments",
  "/employee/hr/dashboard": "HR Dashboard",
  "/employee/hr/employees": "HR Employees",
  "/employee/hr/leave-requests": "Leave Requests",
  "/employee/hr/attendance-approvals": "Attendance Approvals",
  "/employee/hr/performance-reviews": "Performance Reviews",
  "/employee/hr/reports": "HR Reports",
  "/employee/hr/onboarding": "Onboarding",
  "/employee/hr/offboarding": "Offboarding",
  "/employee/hr/recruitment": "Recruitment",
  "/employee/hr/policies": "HR Policies",
  "/employee/hr/training": "Training Programs",
  "/employee/hr/grievance": "Grievance Cases",
  "/employee/hr/vacancies": "Job Vacancies",
  "/employee/hr/interviews": "Interview Schedule",
  "/employee/hr/offer-letters": "Offer Letters",
  "/employee/hr/holidays": "Holiday List",
  "/employee/hr/attendance-rules": "Attendance Rules",
  "/employee/hr/salary-structures": "Salary Structures",
  "/employee/hr/payslips": "Payslips",
  "/employee/hr/resignations": "Resignations",
  "/employee/hr/exit-interviews": "Exit Interviews",
  "/employee/hr/letters": "HR Letters",
  "/employee/hr/documents": "Employee Documents",
  "/employee/hr/payroll-runs": "Payroll Runs",
  "/employee/hr/contractor-work-entries": "Contractor Work Entries",
  "/employee/hr/deduction-rules": "Deduction Rules",
  "/employee/hr/candidates": "Candidate Profiles",
  "/employee/hr/appraisals": "Appraisals",
  "/employee/live-tracking": "Live Tracking",
  "/employee/sales/leads": "Sales Leads",
  "/employee/sales/meta-leads": "Meta Leads",
  "/employee/sales/deals": "Sales Deals",
  "/employee/sales/targets": "Sales Targets",
  "/employee/sales/pricing-approvals": "Pricing Approvals",
  "/employee/sales/reports": "Sales Reports",
  "/employee/sales/dashboard": "Sales Dashboard",
  "/employee/plant/dashboard": "Plant Dashboard",
  "/employee/plant/production-plans": "Production Plans",
  "/employee/plant/production-reports": "Production Reports",
  "/employee/plant/maintenance-logs": "Maintenance Logs",
  "/employee/plant/wastage-logs": "Wastage Logs",
  "/employee/plant/stock-consumption": "Stock Consumption",
  "/employee/plant/shift-plans": "Shift Plans",
  "/employee/plant/overtime-approvals": "Overtime Approvals",
  "/employee/plant/utilization": "Machine Utilization",
  "/employee/plant/breakdowns": "Breakdown Reports",
  "/employee/plant/maintenance-schedules": "Maintenance Schedules",
  "/employee/plant/supplier-quality-workflow": "Supplier Quality Workflow",
  "/employee/plant/quality-reviews": "Quality Reviews",
  "/employee/plant/wastage-analysis": "Wastage Analysis",
  "/employee/plant/material-movement": "Material Movement",
  "/employee/plant/inventory-alerts": "Inventory Alerts",
  "/employee/plant/safety-incidents": "Safety Incidents",
  "/employee/plant/compliance-audits": "Compliance Audits",
  "/employee/plant/coordination-logs": "Coordination Logs",
  "/employee/plant/performance-reports": "Performance Reports",
  "/employee/plant/contractor-work-entries": "Contractor Work Approval",
  "/employee/purchase/dashboard": "Purchase Dashboard",
  "/employee/purchase/requests": "Purchase Requests",
  "/employee/purchase/orders": "Purchase Orders",
  "/employee/purchase/vendor-master": "Vendor Master",
  "/employee/purchase/rate-comparisons": "Rate Comparisons",
  "/employee/purchase/material-availability": "Material Availability",
  "/employee/inventory/stock-ledger": "Stock Ledger",
  "/employee/inventory/grn": "GRN Register",
  "/employee/inventory/issue-return": "Issue & Return",
  "/employee/inventory/adjustments": "Stock Adjustments",
  "/employee/inventory/reports": "Inventory Reports",
  "/employee/logistics/shipments": "Shipments",
  "/employee/logistics/tracking": "Shipment Tracking",
  "/employee/logistics/gatepass": "Gate Pass",
  "/employee/logistics/dispatch-requests": "Dispatch Planning",
  "/employee/logistics/packing-lists": "Packing List",
  "/employee/logistics/transport": "Transport Assignment",
  "/employee/logistics/dispatch-notes": "Dispatch Process",
  "/employee/logistics/returns": "Returns",
  "/employee/logistics/reports": "Logistics Reports",
  "/employee/qa/dashboard": "Quality Dashboard",
  "/employee/qa/checks": "Quality Checks",
  "/employee/qa/rejections": "Rejection Notes",
  "/employee/qa/approvals": "QA Approvals",
  "/employee/qa/reports": "QA Reports",
};

  const financePerms = [
    "finance:invoice:create",
    "finance:invoice:approve",
    "finance:expense:create",
    "finance:expense:approve",
    "finance:vendor-bill:upload",
    "finance:vendor-bill:approve",
    "finance:payments:update",
    "finance:payments:approve",
    "finance:payments:release",
    "finance:gst:view",
    "finance:tds:view",
    "finance:reports:view",
    "finance:audit-logs:view",
    "salary:view",
    "salary:process",
    "salary:approve",
  ];

  const isFinanceUser = financePerms.some((p) => permissions.includes(p));
  const sessionRole = String(localStorage.getItem("role") || user?.role || "").toLowerCase();
  const isGuardUser = sessionRole === "guard";
  const securityPerms = [
    "security:vendor-entry:create",
    "security:material-inward:create",
    "security:vehicle-log:create",
    "security:challan:upload",
    "purchase-order:view",
  ];
  const hasGuardAccess =
    isGuardUser ||
    permissions.includes("guard:access") ||
    securityPerms.some((p) => permissions.includes(p));
  const guardMode = isGuardUser || hasGuardAccess;
  const deptRoleText = `${departmentName} ${roleName}`.toLowerCase();
  const isHrDeptUser = /hr/.test(deptRoleText);
  const isLogisticsDeptUser = /(logistics|dispatch)/.test(deptRoleText);
  const isPlantDeptUser = /(plant|production)/.test(deptRoleText);
  const isPurchaseDeptUser = /(purchase|procurement)/.test(deptRoleText);
  const isPlantPurchaseDeptUser = isPlantDeptUser && isPurchaseDeptUser;
  const isSalesDeptUser = /(sales|marketing)/.test(deptRoleText);
  const isQualityDeptUser = /(quality|qa|qc)/.test(deptRoleText);
  const hasHrScopePerm = permissions.some((p) =>
    typeof p === "string" &&
    (p.startsWith("hr:") ||
      p.startsWith("employees:") ||
      p.startsWith("leave:") ||
      p.startsWith("performance:") ||
      p.startsWith("reports:hr:"))
  );
  const hasHrAccess = isHrDeptUser || hasHrScopePerm;

  const menuItems = [
    ...(isFinanceUser ? [] : [{ path: "/employee/dashboard", label: "📊 Dashboard" }]),
    {
      path: "/employee/attendance",
      label: "📝 Attendance",
      perms: ["attendance:submit", "attendance:view"],
    },
    {
      path: "/employee/salary",
      label: "💰 My Salary",
      perms: [],
    },
    {
      path: "/employee/payment-history",
      label: "🧾 Payment History",
      perms: [],
    },
    {
      path: "/employee/agreement",
      label: "✍️ Agreement",
      perms: [],
    },
    { path: "/employee/my-idcard", label: "🪪 ID Card", perms: ["idcard:view"] },
    { path: "/employee/my-vendors", label: "🧑‍🤝‍🧑 Assigned Vendors", perms: [] },
    { path: "/employee/my-suppliers", label: "🏭 Assigned Suppliers", perms: [] },
    { path: "/employee/my-requirements", label: "📌 Requirements", perms: ["requirements:view"] },
    { path: "/employee/gallery", label: "🖼️ Product Gallery", perms: [] },
    { path: "/employee/reports", label: "📑 Reports", perms: ["reports:create", "reports:view"] },
    { path: "/employee/reports/history", label: "📑 Reports History", perms: ["reports:view"] },
    ...(hasGuardAccess
      ? [
          { path: "/employee/guard/dashboard", label: "🛡️ Guard Dashboard", perms: securityPerms },
          { path: "/employee/guard/attendance", label: "📝 Guard Attendance", perms: securityPerms },
          { path: "/employee/guard/attendance/history", label: "📜 Guard History", perms: securityPerms },
          { path: "/employee/guard/vehicles", label: "🚚 Vehicle Check", perms: securityPerms },
          { path: "/employee/guard/logs", label: "📚 Gate Logs", perms: securityPerms },
        ]
      : []),
    {
      path: "/employee/accounts",
      label: "💰 Accounts",
      perms: financePerms,
    },
    {
      path: "/employee/accounts/parties",
      label: "🏦 Party Ledger",
      perms: financePerms,
    },
    {
      path: "/employee/accounts/invoices",
      label: "🧾 Invoices",
      perms: ["finance:invoice:create", "finance:invoice:approve"],
    },
    {
      path: "/employee/accounts/expenses",
      label: "💳 Expenses",
      perms: ["finance:expense:create", "finance:expense:approve"],
    },
    {
      path: "/employee/accounts/vendor-bills",
      label: "📄 Vendor Bills",
      perms: ["finance:vendor-bill:upload", "finance:vendor-bill:approve"],
    },
    {
      path: "/employee/accounts/quality-payments",
      label: "🧪 Quality Payments",
      perms: ["finance:vendor-bill:upload", "finance:vendor-bill:approve", "finance:payments:update", "finance:payments:approve"],
    },
    {
      path: "/employee/accounts/vendor-incoming-payments",
      label: "📥 Vendor Incoming",
      perms: ["finance:payments:update", "finance:payments:approve", "finance:payments:release", "finance:reports:view"],
    },
    {
      path: "/employee/accounts/supplier-payables",
      label: "📤 Supplier Payables",
      perms: ["finance:vendor-bill:upload", "finance:vendor-bill:approve", "finance:payments:update", "finance:payments:approve"],
    },
    {
      path: "/employee/accounts/payments",
      label: "💸 Payments",
      perms: ["finance:payments:update", "finance:payments:approve", "finance:payments:release"],
    },
    {
      path: "/employee/accounts/government-security",
      label: "🌾 Government Security",
      perms: ["finance:payments:update", "finance:payments:approve", "finance:payments:release"],
    },
    {
      path: "/employee/accounts/phonepe-transactions",
      label: "📲 PhonePe Transactions",
      perms: ["finance:payments:update", "finance:payments:approve", "finance:reports:view"],
    },
    {
      path: "/employee/accounts/payroll",
      label: "🧮 Payroll",
      perms: ["salary:view", "salary:process", "salary:approve"],
    },
    {
      path: "/employee/accounts/worktime",
      label: "⏱️ Worktime & Overtime",
      perms: ["salary:view"],
    },
    {
      path: "/employee/accounts/contractor-work-entries",
      label: "👷 Contractor Payments",
      perms: ["salary:view", "salary:process", "salary:approve"],
    },
    {
      path: "/employee/accounts/taxes",
      label: "🧾 GST / TDS",
      perms: ["finance:gst:view", "finance:tds:view"],
    },
    {
      path: "/employee/accounts/reports",
      label: "📊 Financial Reports",
      perms: ["finance:reports:view"],
    },
    {
      path: "/employee/accounts/audit-logs",
      label: "🧿 Audit Logs",
      perms: ["finance:audit-logs:view"],
    },
    {
      path: "/employee/hr/dashboard",
      label: "🧑‍💼 HR Dashboard",
      perms: ["employees:view", "leave:view", "attendance:view", "performance:view"],
    },
    {
      path: "/employee/hr/employees",
      label: "🧑‍💼 HR Employees",
      perms: ["employees:view", "employees:add", "employees:edit"],
    },
    {
      path: "/employee/hr/leave-requests",
      label: "🌴 Leave Requests",
      perms: ["leave:view", "leave:approve"],
    },
    {
      path: "/employee/hr/attendance-approvals",
      label: "✅ Attendance Approvals",
      perms: ["attendance:view", "attendance:approve"],
    },
    {
      path: "/employee/hr/performance-reviews",
      label: "⭐ Performance Reviews",
      perms: ["performance:view"],
    },
    {
      path: "/employee/hr/reports",
      label: "📑 HR Reports",
      perms: ["reports:hr:view"],
    },
    {
      path: "/employee/hr/onboarding",
      label: "🧾 Onboarding",
      perms: ["hr:onboarding:manage"],
    },
    {
      path: "/employee/hr/offboarding",
      label: "📤 Offboarding",
      perms: ["hr:offboarding:manage"],
    },
    {
      path: "/employee/hr/recruitment",
      label: "🎯 Recruitment",
      perms: ["hr:recruitment:manage"],
    },
    {
      path: "/employee/hr/policies",
      label: "📘 HR Policies",
      perms: ["hr:policies:manage"],
    },
    {
      path: "/employee/hr/training",
      label: "🎓 Training",
      perms: ["hr:training:manage"],
    },
    {
      path: "/employee/hr/grievance",
      label: "🧩 Grievance",
      perms: ["hr:grievance:manage"],
    },
    {
      path: "/employee/hr/vacancies",
      label: "📌 Job Vacancies",
      perms: ["hr:vacancy:manage"],
    },
    {
      path: "/employee/hr/interviews",
      label: "🗓️ Interviews",
      perms: ["hr:interview:manage"],
    },
    {
      path: "/employee/hr/offer-letters",
      label: "✉️ Offer Letters",
      perms: ["hr:offer-letter:manage"],
    },
    {
      path: "/employee/hr/holidays",
      label: "📅 Holiday List",
      perms: ["hr:holiday:manage"],
    },
    {
      path: "/employee/hr/attendance-rules",
      label: "⏱️ Attendance Rules",
      perms: ["hr:attendance-rule:manage"],
    },
    {
      path: "/employee/hr/salary-structures",
      label: "💼 Salary Structures",
      perms: ["hr:salary-structure:manage"],
    },
    {
      path: "/employee/hr/payslips",
      label: "🧾 Payslips",
      perms: ["hr:payslip:manage"],
    },
    {
      path: "/employee/hr/resignations",
      label: "📤 Resignations",
      perms: ["hr:resignation:manage"],
    },
    {
      path: "/employee/hr/exit-interviews",
      label: "🗣️ Exit Interviews",
      perms: ["hr:exit-interview:manage"],
    },
    {
      path: "/employee/hr/letters",
      label: "📄 HR Letters",
      perms: ["hr:letters:manage"],
    },
    {
      path: "/employee/hr/documents",
      label: "📂 Documents",
      perms: ["hr:documents:view", "employees:documents:upload"],
    },
    {
      path: "/employee/hr/payroll-runs",
      label: "🧮 Payroll Runs",
      perms: ["hr:payroll:manage"],
    },
    {
      path: "/employee/hr/contractor-work-entries",
      label: "👷 Contractor Work",
      perms: ["hr:payroll:manage"],
    },
    {
      path: "/employee/hr/deduction-rules",
      label: "🧾 Deduction Rules",
      perms: ["hr:deduction:manage"],
    },
    {
      path: "/employee/hr/candidates",
      label: "🧑‍💼 Candidates",
      perms: ["hr:candidate:manage"],
    },
    {
      path: "/employee/hr/appraisals",
      label: "📈 Appraisals",
      perms: ["hr:appraisal:manage"],
    },
    {
      path: "/employee/live-tracking",
      label: "📍 Live Tracking",
      perms: [],
    },
    {
      path: "/employee/sales/leads",
      label: "📞 Sales Leads",
      perms: ["leads:view", "leads:edit"],
    },
    {
      path: "/employee/sales/dashboard",
      label: "📊 Sales Dashboard",
      perms: ["leads:view", "leads:edit", "deals:view", "deals:edit", "sales:targets:manage", "pricing:approve", "discount:approve", "reports:sales:view"],
    },
    {
      path: "/employee/sales/meta-leads",
      label: "📣 Meta Leads",
      perms: ["leads:view", "leads:edit"],
    },
    {
      path: "/employee/sales/deals",
      label: "🤝 Sales Deals",
      perms: ["deals:view", "deals:edit"],
    },
    {
      path: "/employee/sales/targets",
      label: "🎯 Sales Targets",
      perms: ["sales:targets:manage"],
    },
    {
      path: "/employee/sales/pricing-approvals",
      label: "💵 Pricing Approvals",
      perms: ["pricing:approve", "discount:approve"],
    },
    {
      path: "/employee/sales/reports",
      label: "📈 Sales Reports",
      perms: ["reports:sales:view"],
    },
    {
      path: "/employee/plant/dashboard",
      label: "🏭 Plant Dashboard",
      perms: [
        "production:plan",
        "production:report",
        "stock:consumption:view",
        "maintenance:log",
        "wastage:view",
        "plant:shift:manage",
        "plant:overtime:approve",
        "plant:utilization:view",
        "plant:breakdown:view",
        "plant:maintenance:approve",
        "plant:inventory:alert",
        "plant:material-movement:approve",
        "plant:contractor-work:approve",
        "plant:safety:manage",
        "plant:compliance:manage",
        "plant:coordination:manage",
        "plant:reports:manage",
      ],
    },
    {
      path: "/employee/plant/production-plans",
      label: "🏭 Production Plans",
      perms: ["production:plan"],
    },
    {
      path: "/employee/plant/production-reports",
      label: "📋 Production Reports",
      perms: ["production:report"],
    },
    {
      path: "/employee/plant/maintenance-logs",
      label: "🛠️ Maintenance Logs",
      perms: ["maintenance:log"],
    },
    {
      path: "/employee/plant/wastage-logs",
      label: "♻️ Wastage Logs",
      perms: ["wastage:view"],
    },
    {
      path: "/employee/plant/stock-consumption",
      label: "📦 Stock Consumption",
      perms: ["stock:consumption:view"],
    },
    {
      path: "/employee/plant/shift-plans",
      label: "🗓️ Shift Plans",
      perms: ["plant:shift:manage"],
    },
    {
      path: "/employee/plant/overtime-approvals",
      label: "⏱️ Overtime Approvals",
      perms: ["plant:overtime:approve"],
    },
    {
      path: "/employee/plant/utilization",
      label: "📈 Utilization",
      perms: ["plant:utilization:view"],
    },
    {
      path: "/employee/plant/breakdowns",
      label: "🧰 Breakdowns",
      perms: ["plant:breakdown:view"],
    },
    {
      path: "/employee/plant/maintenance-schedules",
      label: "🛠️ Maintenance Schedules",
      perms: ["plant:maintenance:approve"],
    },
    {
      path: "/employee/plant/supplier-quality-workflow",
      label: "🧪 Supplier Quality Workflow",
      perms: ["plant:quality:review", "quality:approve"],
    },
    {
      path: "/employee/plant/quality-reviews",
      label: "✅ Quality Reviews",
      perms: ["quality:check:view", "quality:check:create", "quality:approve"],
    },
    {
      path: "/employee/plant/wastage-analysis",
      label: "♻️ Wastage Analysis",
      perms: ["wastage:view"],
    },
    {
      path: "/employee/plant/material-movement",
      label: "↔️ Material Movement",
      perms: ["plant:material-movement:approve"],
    },
    {
      path: "/employee/plant/inventory-alerts",
      label: "⚠️ Inventory Alerts",
      perms: ["plant:inventory:alert"],
    },
    {
      path: "/employee/plant/safety-incidents",
      label: "🦺 Safety Incidents",
      perms: ["plant:safety:manage"],
    },
    {
      path: "/employee/plant/compliance-audits",
      label: "📋 Compliance Audits",
      perms: ["plant:compliance:manage"],
    },
    {
      path: "/employee/plant/coordination-logs",
      label: "🤝 Coordination Logs",
      perms: ["plant:coordination:manage"],
    },
    {
      path: "/employee/plant/performance-reports",
      label: "📊 Performance Reports",
      perms: ["plant:reports:manage"],
    },
    {
      path: "/employee/plant/contractor-work-entries",
      label: "👷 Contractor Work Approval",
      perms: ["plant:contractor-work:approve"],
    },
    {
      path: "/employee/purchase/dashboard",
      label: "🛒 Purchase Dashboard",
      perms: [
        "purchase:request:create",
        "purchase:order:create",
        "vendor-master:edit",
        "purchase:rate-comparison:view",
        "material:availability:view",
        "vendors:view",
      ],
    },
    {
      path: "/employee/purchase/requests",
      label: "🧾 Purchase Requests",
      perms: ["purchase:request:create"],
    },
    {
      path: "/employee/purchase/orders",
      label: "🧾 Purchase Orders",
      perms: ["purchase:order:create"],
    },
    {
      path: "/employee/purchase/vendor-master",
      label: "🏷️ Vendor Master",
      perms: ["vendor-master:edit"],
    },
    {
      path: "/employee/purchase/rate-comparisons",
      label: "📊 Rate Comparisons",
      perms: ["purchase:rate-comparison:view"],
    },
    {
      path: "/employee/purchase/material-availability",
      label: "📦 Material Availability",
      perms: ["material:availability:view"],
    },
    {
      path: "/employee/inventory/stock-ledger",
      label: "📚 Stock Ledger",
      perms: ["inventory:stock:view", "inventory:stock:update"],
    },
    {
      path: "/employee/inventory/grn",
      label: "📥 GRN Register",
      perms: ["inventory:grn:create"],
    },
    {
      path: "/employee/inventory/issue-return",
      label: "🔁 Issue & Return",
      perms: ["inventory:issue:return"],
    },
    {
      path: "/employee/inventory/adjustments",
      label: "🧮 Stock Adjustments",
      perms: ["inventory:stock:update"],
    },
    {
      path: "/employee/inventory/reports",
      label: "📊 Inventory Reports",
      perms: ["inventory:reports:view"],
    },
    {
      path: "/employee/logistics/shipments",
      label: "🚚 Shipments",
      perms: ["logistics:shipment:view", "logistics:shipment:create"],
    },
    {
      path: "/employee/logistics/dispatch-requests",
      label: "🧾 Dispatch Planning",
      perms: ["logistics:planning:manage"],
    },
    {
      path: "/employee/logistics/packing-lists",
      label: "📦 Packing & Labels",
      perms: ["logistics:packing:manage"],
    },
    {
      path: "/employee/logistics/transport",
      label: "🚛 Transport",
      perms: ["logistics:transport:manage"],
    },
    {
      path: "/employee/logistics/dispatch-notes",
      label: "📝 Dispatch Process",
      perms: ["logistics:dispatch:manage"],
    },
    {
      path: "/employee/logistics/tracking",
      label: "📍 Shipment Tracking",
      perms: ["logistics:tracking:manage"],
    },
    {
      path: "/employee/logistics/returns",
      label: "↩️ Returns",
      perms: ["logistics:returns:manage"],
    },
    {
      path: "/employee/logistics/reports",
      label: "📊 Logistics Reports",
      perms: ["logistics:reports:manage"],
    },
    {
      path: "/employee/qa/dashboard",
      label: "📊 Quality Dashboard",
      perms: ["quality:check:view", "quality:check:create", "quality:reject:create", "quality:approve", "quality:reports:view"],
    },
    {
      path: "/employee/qa/checks",
      label: "🔍 Quality Checks",
      perms: ["quality:check:view", "quality:check:create"],
    },
    {
      path: "/employee/qa/rejections",
      label: "⛔ Rejection Notes",
      perms: ["quality:reject:create"],
    },
    {
      path: "/employee/qa/approvals",
      label: "✅ QA Approvals",
      perms: ["quality:approve"],
    },
    {
      path: "/employee/qa/reports",
      label: "📑 QA Reports",
      perms: ["quality:reports:view"],
    },
    { path: "/employee/profile", label: "👤 Profile" },
  ];

  const hrOnlyPaths = new Set([
    "/employee/hr/dashboard",
    "/employee/hr/employees",
    "/employee/hr/leave-requests",
    "/employee/hr/attendance-approvals",
    "/employee/hr/performance-reviews",
    "/employee/hr/reports",
    "/employee/hr/onboarding",
    "/employee/hr/offboarding",
    "/employee/hr/recruitment",
    "/employee/hr/policies",
    "/employee/hr/training",
    "/employee/hr/grievance",
    "/employee/hr/vacancies",
    "/employee/hr/interviews",
    "/employee/hr/offer-letters",
    "/employee/hr/holidays",
    "/employee/hr/attendance-rules",
    "/employee/hr/salary-structures",
    "/employee/hr/payslips",
    "/employee/hr/resignations",
    "/employee/hr/exit-interviews",
    "/employee/hr/letters",
    "/employee/hr/documents",
    "/employee/hr/payroll-runs",
    "/employee/hr/contractor-work-entries",
    "/employee/hr/deduction-rules",
    "/employee/hr/candidates",
    "/employee/hr/appraisals",
    "/employee/my-vendors",
    "/employee/live-tracking",
    "/employee/attendance",
    "/employee/my-idcard",
    "/employee/salary",
    "/employee/agreement",
    "/employee/profile",
  ]);
  const logisticsOnlyPaths = new Set([
    "/employee/logistics/shipments",
    "/employee/logistics/dispatch-requests",
    "/employee/logistics/packing-lists",
    "/employee/logistics/transport",
    "/employee/logistics/dispatch-notes",
    "/employee/logistics/tracking",
    "/employee/logistics/returns",
    "/employee/logistics/reports",
    "/employee/my-vendors",
    "/employee/attendance",
    "/employee/my-idcard",
    "/employee/salary",
    "/employee/agreement",
    "/employee/profile",
  ]);
  const plantOnlyPaths = new Set([
    "/employee/plant/dashboard",
    "/employee/plant/production-plans",
    "/employee/plant/production-reports",
    "/employee/plant/maintenance-logs",
    "/employee/plant/wastage-logs",
    "/employee/plant/stock-consumption",
    "/employee/plant/shift-plans",
    "/employee/plant/overtime-approvals",
    "/employee/plant/utilization",
    "/employee/plant/breakdowns",
    "/employee/plant/maintenance-schedules",
    "/employee/plant/supplier-quality-workflow",
    "/employee/plant/wastage-analysis",
    "/employee/plant/material-movement",
    "/employee/plant/inventory-alerts",
    "/employee/plant/safety-incidents",
    "/employee/plant/compliance-audits",
    "/employee/plant/coordination-logs",
    "/employee/plant/performance-reports",
    "/employee/plant/contractor-work-entries",
    "/employee/my-vendors",
    "/employee/attendance",
    "/employee/my-idcard",
    "/employee/salary",
    "/employee/agreement",
    "/employee/profile",
  ]);
  const purchaseOnlyPaths = new Set([
    "/employee/purchase/dashboard",
    "/employee/purchase/requests",
    "/employee/purchase/orders",
    "/employee/purchase/vendor-master",
    "/employee/purchase/rate-comparisons",
    "/employee/purchase/material-availability",
    "/employee/inventory/stock-ledger",
    "/employee/inventory/grn",
    "/employee/inventory/issue-return",
    "/employee/inventory/adjustments",
    "/employee/inventory/reports",
    "/employee/my-vendors",
    "/employee/attendance",
    "/employee/my-idcard",
    "/employee/salary",
    "/employee/agreement",
    "/employee/profile",
  ]);
  const plantPurchasePaths = new Set([
    ...Array.from(plantOnlyPaths),
    ...Array.from(purchaseOnlyPaths),
  ]);
  const salesOnlyPaths = new Set([
    "/employee/sales/dashboard",
    "/employee/sales/leads",
    "/employee/sales/meta-leads",
    "/employee/sales/deals",
    "/employee/sales/targets",
    "/employee/sales/pricing-approvals",
    "/employee/sales/reports",
    "/employee/my-vendors",
    "/employee/live-tracking",
    "/employee/attendance",
    "/employee/my-idcard",
    "/employee/salary",
    "/employee/agreement",
    "/employee/profile",
  ]);
  const qualityOnlyPaths = new Set([
    "/employee/qa/dashboard",
    "/employee/plant/quality-reviews",
    "/employee/qa/checks",
    "/employee/qa/rejections",
    "/employee/qa/approvals",
    "/employee/qa/reports",
    "/employee/my-vendors",
    "/employee/attendance",
    "/employee/my-idcard",
    "/employee/gallery",
    "/employee/reports",
    "/employee/reports/history",
    "/employee/salary",
    "/employee/payment-history",
    "/employee/agreement",
    "/employee/profile",
  ]);

  const canSee = (item) => {
    if (String(item.path || "").startsWith("/employee/guard/")) return hasGuardAccess;
    if (String(item.path || "").startsWith("/employee/hr/") && !hasHrAccess) return false;
    if (isSalesDeptUser && item.path === "/employee/sales/dashboard") return true;
    if (!item.perms || item.perms.length === 0) return true;
    return item.perms.some((p) => permissions.includes(p));
  };

  const guardPathMode = location.pathname.startsWith("/employee/guard/");
  const visibleMenu = ((guardMode || guardPathMode)
    ? menuItems.filter((item) =>
        String(item.path || "").startsWith("/employee/guard/") ||
        item.path === "/employee/profile" ||
        item.path === "/employee/my-vendors" ||
        item.path === "/employee/salary" ||
        item.path === "/employee/agreement" ||
        item.path === "/employee/payment-history"
      )
    : isHrDeptUser
    ? menuItems.filter((item) => hrOnlyPaths.has(item.path))
    : isLogisticsDeptUser
      ? menuItems.filter((item) => logisticsOnlyPaths.has(item.path))
    : isQualityDeptUser
      ? menuItems.filter((item) => qualityOnlyPaths.has(item.path))
    : isPlantPurchaseDeptUser
      ? menuItems.filter((item) => plantPurchasePaths.has(item.path))
      : isPlantDeptUser
        ? menuItems.filter((item) => plantOnlyPaths.has(item.path))
        : isPurchaseDeptUser
          ? menuItems.filter((item) => purchaseOnlyPaths.has(item.path))
        : isSalesDeptUser
          ? menuItems.filter((item) => salesOnlyPaths.has(item.path))
        : menuItems
  ).filter(canSee);

  useEffect(() => {
    if (!permReady) return;
    const path = location.pathname;
    if (!path.startsWith("/employee")) return;
    if (path === "/employee" || path === "/employee/") return;
    if (path === "/employee/notifications") return;
    const allowed = visibleMenu.some(
      (item) => path === item.path || path.startsWith(`${item.path}/`)
    );
    if (allowed) return;

    const preferredQualityHome = visibleMenu.find((item) => item.path === "/employee/qa/dashboard")?.path;
    const preferredPlantHome = visibleMenu.find((item) => item.path === "/employee/plant/dashboard")?.path;
    const preferredPurchaseHome = visibleMenu.find((item) => item.path === "/employee/purchase/dashboard")?.path;
    const fallback = preferredQualityHome || preferredPlantHome || preferredPurchaseHome || visibleMenu[0]?.path || "/employee/dashboard";
    navigate(fallback, { replace: true });
  }, [
    permReady,
    location.pathname,
    visibleMenu,
    navigate,
  ]);

  useEffect(() => {
    if (!permReady) return;
    if (!(guardMode || guardPathMode)) return;
    const path = location.pathname;
    if (
      path.startsWith("/employee/guard/") ||
      path === "/employee/profile" ||
      path === "/employee/notifications" ||
      path === "/employee/salary" ||
      path === "/employee/payment-history"
    ) return;
    navigate("/employee/guard/dashboard", { replace: true });
  }, [permReady, guardMode, guardPathMode, location.pathname, navigate]);

  const handleNav = (path) => {
    navigate(path);
    setSidebarVisible(false);
  };


  const currentPath = location.pathname;
  const isGuardRoute = currentPath.startsWith("/employee/guard/");
  const isAccountsRoute = currentPath.startsWith("/employee/accounts");
  const isDashboardRoute =
    currentPath === "/employee" ||
    currentPath === "/employee/" ||
    (currentPath.startsWith("/employee/") && currentPath.endsWith("/dashboard")) ||
    currentPath === "/employee/dashboard" ||
    currentPath === "/employee/guard/dashboard";
  const isBottomActive = (prefixes = [], options = {}) =>
    prefixes.some(
      (p) =>
        currentPath === p || (!options.exact && currentPath.startsWith(`${p}/`))
    );
  const isAccountsNavItemActive = (path) =>
    path === "/employee/accounts"
      ? isBottomActive([path], { exact: true })
      : isBottomActive([path]);
  const currentTitle =
    routeTitles[
      Object.keys(routeTitles).find((path) => currentPath.startsWith(path))
    ] || "";
  const sidebarLabel = (label = "") => String(label).replace(/^[^\p{L}\p{N}]+/u, "").trim();
  const desktopNavLabel = (label = "") =>
    sidebarLabel(label)
      .replace(/^GST \/ TDS$/i, "Taxes")
      .replace(/^Financial Reports$/i, "Reports")
      .replace(/^PhonePe Transactions$/i, "PhonePe");
  const sidebarIconFor = (path = "") => {
    if (path.includes("/meta-leads")) return "campaign";
    if (path.includes("/dashboard")) return "space_dashboard";
    if (path.includes("/attendance")) return "event_available";
    if (path.includes("/salary")) return "payments";
    if (path.includes("/agreement")) return "contract_edit";
    if (path.includes("/accounts/phonepe-transactions")) return "payments";
    if (path.includes("/idcard")) return "badge";
    if (path.includes("/vendors")) return "storefront";
    if (path.includes("/suppliers")) return "local_shipping";
    if (path.includes("/requirements")) return "checklist";
    if (path.includes("/reports/history")) return "history";
    if (path.includes("/reports")) return "query_stats";
    if (path.includes("/accounts/audit-logs")) return "policy";
    if (path.includes("/accounts/parties")) return "account_balance_wallet";
    if (path.includes("/accounts/invoices")) return "receipt_long";
    if (path.includes("/accounts/payroll")) return "account_balance";
    if (path.includes("/accounts/taxes")) return "request_quote";
    if (path.includes("/accounts")) return "account_balance_wallet";
    if (path.includes("/hr/")) return "badge";
    if (path.includes("/sales/")) return "monitoring";
    if (path.includes("/plant/")) return "factory";
    if (path.includes("/purchase/")) return "shopping_cart";
    if (path.includes("/inventory/")) return "inventory";
    if (path.includes("/logistics/")) return "local_shipping";
    if (path.includes("/qa/")) return "task_alt";
    if (path.includes("/employee/guard/")) return "shield";
    if (path.includes("/profile")) return "account_circle";
    return "chevron_right";
  };
  const accountsShortcutPaths = new Set([
    "/employee/attendance",
    "/employee/my-idcard",
  ]);
  const accountsMenu = visibleMenu.filter(
    (item) =>
      String(item.path || "").startsWith("/employee/accounts") ||
      accountsShortcutPaths.has(item.path)
  );
  const handleGoBack = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(isGuardRoute ? "/employee/guard/dashboard" : "/employee/dashboard");
  };

  const userName = user?.fullName || "Employee";
  const userInitials = useMemo(
    () =>
      String(userName)
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0]?.toUpperCase() || "")
        .join("") || "E",
    [userName]
  );
  const userPhoto = resolveProfilePhoto(
    user?.photo,
    user?.photoUpdatedAt || user?.updatedAt || ""
  );

  return (
    <div
      className="panel-layout-shell"
      style={{
        display: "flex",
        flexDirection: isAccountsRoute ? "column" : "row",
        height: "100vh",
        "--role-sidebar-gradient": "linear-gradient(180deg, #14532d 0%, #22c55e 100%)",
        "--role-sidebar-border": "#166534",
        "--role-title-color": "#16a34a",
        "--panel-accent-rgb": "34, 197, 94",
        "--panel-nav-bg": "#14532d",
        "--panel-nav-border": "rgba(255, 255, 255, 0.16)",
        "--panel-nav-shadow": "0 -10px 30px rgba(20, 83, 45, 0.32)",
        "--panel-nav-active-bg": "#ffffff",
        "--panel-nav-active-color": "#14532d",
        "--panel-nav-active-shadow": "0 2px 8px rgba(20, 83, 45, 0.2)",
      }}
    >
      {/* Sidebar (desktop) */}
      <div
        className={`d-none d-md-block ${isAccountsRoute ? "panel-layout-sidebar-hidden" : ""}`}
        style={{ width: "240px", background: "var(--role-sidebar-gradient)", height: "100vh", display: "flex", flexDirection: "column" }}
      >
        {/* ✅ Branding Header */}
        <div
          style={{
            color: "white",
            fontSize: "22px",
            fontWeight: "bold",
            padding: "8.5px",
            borderBottom: "2px solid var(--role-sidebar-border)",
            textAlign: "center",
            letterSpacing: "1px",
          }}
        >
          🌾 Lotus Rice Mill Pvt. Ltd.
          <div style={{ fontSize: "14px", fontWeight: "normal", marginTop: "4px" }}>
           
          </div>
        </div>

        {/* Sidebar Menu */}
        <div
          className="d-flex flex-column p-3 employee-sidebar-scroll"
          style={{ flex: 1, overflowY: "auto", maxHeight: "calc(100vh - 64px)" }}
        >
          <div className="panel-sidebar-menu">
            {visibleMenu.map((item) => (
              <button
                key={item.path}
                type="button"
                className={`panel-sidebar-item ${isBottomActive([item.path]) ? "active" : ""}`}
                onClick={() => handleNav(item.path)}
              >
                <span className="material-symbols-outlined">{sidebarIconFor(item.path)}</span>
                <span>{sidebarLabel(item.label)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Sidebar Offcanvas (mobile) */}
      <Offcanvas
        show={sidebarVisible}
        onHide={() => setSidebarVisible(false)}
        className="panel-mobile-drawer"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>🌾 Lotus Rice Mill Pvt. Ltd.</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body
          className="employee-offcanvas-scroll"
          style={{
            background: "linear-gradient(180deg, #14532d 0%, #22c55e 100%)",
            color: "#fff",
            overflowY: "auto",
            maxHeight: "100vh",
          }}
        >
          <div className="panel-sidebar-menu">
            {visibleMenu.map((item) => (
              <button
                key={item.path}
                type="button"
                className={`panel-sidebar-item ${isBottomActive([item.path]) ? "active" : ""}`}
                onClick={() => handleNav(item.path)}
              >
                <span className="material-symbols-outlined">{sidebarIconFor(item.path)}</span>
                <span>{sidebarLabel(item.label)}</span>
              </button>
            ))}
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Topbar + Content */}
      <div className={`panel-layout-maincol ${isAccountsRoute ? "panel-layout-maincol-accounts" : ""}`} style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {/* TOPBAR */}
        <div
          className={`panel-layout-topbar ${isAccountsRoute ? "panel-layout-topbar-accounts" : ""}`}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 24px",
            backgroundColor: isAccountsRoute ? "#10b981" : "var(--app-topbar)",
            borderBottom: "1px solid #dee2e6",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {!isDashboardRoute && (
              <Button
                variant="light"
                className="d-md-none"
                onClick={handleGoBack}
                aria-label="Go back"
              >
                <span className="material-symbols-outlined">arrow_back_ios_new</span>
              </Button>
            )}
            <Button
              variant="light"
              className="d-md-none"
              onClick={() => setSidebarVisible(true)}
            >
              <BsList />
            </Button>
            <div
              className="panel-mobile-title"
              style={{ fontSize: "18px", fontWeight: 600, color: isAccountsRoute ? "#ffffff" : "var(--role-title-color)" }}
            >
              {isAccountsRoute ? "Lotus Rice Mills Private Limited" : currentTitle}
            </div>
            {isAccountsRoute && (
              <div className="panel-layout-accounts-title d-none d-md-flex">
                <span className="material-symbols-outlined">monitoring</span>
                <div>
                  <strong>Accounts Dashboard</strong>
                  <span>{currentTitle}</span>
                </div>
              </div>
            )}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "20px",
              position: "relative",
            }}
          >
            <div
              style={{ position: "relative", cursor: "pointer" }}
              onClick={() => navigate("/employee/notifications")}
            >
              <BsBellFill size={20} className={isAccountsRoute ? "text-white" : "text-secondary"} />
              {notificationCount > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: "-6px",
                    right: "-10px",
                    background: "red",
                    color: "white",
                    fontSize: "10px",
                    borderRadius: "50%",
                    padding: "2px 6px",
                  }}
                >
                  {notificationCount}
                </span>
              )}
            </div>

            <Dropdown>
              <Dropdown.Toggle
                variant="light"
                size="sm"
                className={isAccountsRoute ? "panel-layout-accounts-user" : ""}
                style={{
                  fontWeight: "bold",
                  zIndex: 10001,
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {userPhoto ? (
                  <img
                    src={userPhoto}
                    alt={userName}
                    style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover" }}
                  />
                ) : (
                  <span
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: "50%",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#e2e8f0",
                      color: "#334155",
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {userInitials}
                  </span>
                )}
                <span className="panel-mobile-user-name">{userName}</span>
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ zIndex: 10002, position: 'absolute' }}>
                <Dropdown.Item onClick={() => navigate("/employee/profile")}>Profile</Dropdown.Item>
                <Dropdown.Item onClick={handleLogout}>Logout</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        {isAccountsRoute && (
          <div className="panel-layout-accounts-nav d-none d-md-flex">
            {accountsMenu.map((item) => (
              <button
                key={item.path}
                type="button"
                className={`panel-layout-accounts-navitem ${isAccountsNavItemActive(item.path) ? "active" : ""}`}
                onClick={() => handleNav(item.path)}
              >
                <span className="material-symbols-outlined">{sidebarIconFor(item.path)}</span>
                <span>{desktopNavLabel(item.label)}</span>
              </button>
            ))}
          </div>
        )}

        <GstApiDueNotice />

        {/* CONTENT */}
        <div
          className={`panel-layout-content ${isAccountsRoute ? "panel-layout-content-accounts" : ""}`}
          style={{
            flex: 1,
            overflowY: "auto",
            background: "var(--app-bg)",
            padding: "20px",
        
          }}
        >
          <Outlet />
        </div>

        <div className="panel-mobile-nav" style={{ "--panel-nav-count": 5 }}>
          {guardMode || isGuardRoute ? (
            <>
              <button
                className={`panel-mobile-nav-item ${isBottomActive(["/employee/guard/dashboard"]) ? "active" : ""}`}
                onClick={() => navigate("/employee/guard/dashboard")}
              >
                <span className="material-symbols-outlined">security</span>
                <span>Home</span>
              </button>
              <button
                className={`panel-mobile-nav-item ${isBottomActive(["/employee/guard/attendance"], { exact: true }) ? "active" : ""}`}
                onClick={() => navigate("/employee/guard/attendance")}
              >
                <span className="material-symbols-outlined">fingerprint</span>
                <span>Punch</span>
              </button>
              <button
                className={`panel-mobile-nav-item ${isBottomActive(["/employee/guard/attendance/history"]) ? "active" : ""}`}
                onClick={() => navigate("/employee/guard/attendance/history")}
              >
                <span className="material-symbols-outlined">history</span>
                <span>History</span>
              </button>
              <button
                className={`panel-mobile-nav-item ${isBottomActive(["/employee/guard/vehicles"]) ? "active" : ""}`}
                onClick={() => navigate("/employee/guard/vehicles")}
              >
                <span className="material-symbols-outlined">local_shipping</span>
                <span>Vehicle</span>
              </button>
              <button
                className={`panel-mobile-nav-item ${isBottomActive(["/employee/profile"]) ? "active" : ""}`}
                onClick={() => navigate("/employee/profile")}
              >
                <span className="material-symbols-outlined">person</span>
                <span>Profile</span>
              </button>
            </>
          ) : (
            <>
              <button
                className={`panel-mobile-nav-item ${isBottomActive(["/employee/dashboard", "/employee/hr/dashboard", "/employee/sales/dashboard", "/employee/plant/dashboard", "/employee/purchase/dashboard", "/employee/qa/dashboard"]) ? "active" : ""}`}
                onClick={() =>
                  navigate(
                    isQualityDeptUser
                      ? "/employee/qa/dashboard"
                      : isPlantDeptUser && !isPurchaseDeptUser
                      ? "/employee/plant/dashboard"
                      : isPurchaseDeptUser
                        ? "/employee/purchase/dashboard"
                        : "/employee/dashboard"
                  )
                }
              >
                <span className="material-symbols-outlined">dashboard</span>
                <span>Dashboard</span>
              </button>
              <button
                className={`panel-mobile-nav-item ${isBottomActive([isQualityDeptUser ? "/employee/qa/checks" : "/employee/my-vendors"]) ? "active" : ""}`}
                onClick={() => navigate(isQualityDeptUser ? "/employee/qa/checks" : "/employee/my-vendors")}
              >
                <span className="material-symbols-outlined">{isQualityDeptUser ? "task_alt" : "groups"}</span>
                <span>{isQualityDeptUser ? "Checks" : "Vendors"}</span>
              </button>
              <button
                className={`panel-mobile-nav-item ${isBottomActive(["/employee/attendance"]) ? "active" : ""}`}
                onClick={() => navigate("/employee/attendance")}
              >
                <span className="material-symbols-outlined">event_available</span>
                <span>Attend</span>
              </button>
              <button
                className={`panel-mobile-nav-item ${isBottomActive([isQualityDeptUser ? "/employee/qa/reports" : "/employee/sales/meta-leads"]) ? "active" : ""}`}
                onClick={() => navigate(isQualityDeptUser ? "/employee/qa/reports" : "/employee/sales/meta-leads")}
              >
                <span className="material-symbols-outlined">{isQualityDeptUser ? "query_stats" : "campaign"}</span>
                <span>{isQualityDeptUser ? "Reports" : "Leads"}</span>
              </button>
              <button
                className={`panel-mobile-nav-item ${isBottomActive(["/employee/profile"]) ? "active" : ""}`}
                onClick={() => navigate("/employee/profile")}
              >
                <span className="material-symbols-outlined">person</span>
                <span>Profile</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeMainLayout;
